import { useEffect, useRef } from "react";
import { useAI } from "../context/AIContext";

import { getStoredOidcToken } from "./utility/fetchWrapper";

export default function BackgroundRunner({ API_BASE_URL, sessionId, accountId }) {
  
  // ============================================
  // GET STATE FROM CONTEXT
  // ============================================
  const {
    status,           // Current task status
    prompt,           // User's question
    model,            // AI model to use
    updatePartial,    // Function to update streaming content
    setFinished,      // Function to mark task complete
    setError,         // Function to handle errors
    abortRef,         // Ref to AbortController
    activeRunIdRef,   // Ref to active generation id
    startTimeRef,     // Ref to task start time
    currentMessageIdRef, // Ref to current message being updated
    addMessageMetadata   // Function to add metadata (thinking, follow-ups)
  } = useAI();
  
  // ============================================
  // TRACK IF WE'RE ALREADY PROCESSING
  // ============================================
  // Prevents duplicate requests if useEffect runs multiple times
  const isProcessingRef = useRef(false);
  
  // ============================================
  // MAIN EFFECT - WATCHES FOR STATUS CHANGES
  // ============================================
  useEffect(() => {
    // Only proceed if status is "running" and we have a prompt
    if (status !== "running") {
      isProcessingRef.current = false;
      return;
    }

    const runId = activeRunIdRef.current;
    const messageId = currentMessageIdRef.current;

    if (!runId || !messageId) {
      isProcessingRef.current = false;
      return;
    }
    
    if (!prompt || !prompt.trim()) {
      console.warn("Status is running but no prompt provided");
      setError("No prompt provided", runId, messageId);
      return;
    }
    
    // Prevent duplicate processing
    if (isProcessingRef.current) {
      console.warn("Already processing a request, skipping");
      return;
    }
    
    isProcessingRef.current = true;
    console.log("🚀 BackgroundRunner starting task:", { prompt, model });
    
    // ============================================
    // USE EXISTING ABORT CONTROLLER (set in startTask) OR CREATE ONE
    // ============================================
    const controller = abortRef.current || new AbortController();
    abortRef.current = controller;
    
    // ============================================
    // MAIN ASYNC FUNCTION
    // ============================================
    const runTask = async () => {
      let fullContent = "";
      
      try {
        // ============================================
        // MAKE API REQUEST
        // ============================================
        console.log("Making API request to:", `${API_BASE_URL}/v1/chat/completions`);

        // Prefer prop, then last-selected account from Accounts/Metrics pages
        let resolvedAccountId = accountId;
        if (!resolvedAccountId) {
          try {
            resolvedAccountId =
              localStorage.getItem("clairai_selected_aws_account_id") ||
              localStorage.getItem("selectedAwsAccountId") ||
              "";
          } catch {
            resolvedAccountId = "";
          }
        }
        
        const headers = {
            "Content-Type": "application/json",
            "accept": "application/json",
            "X-Openwebui-Chat-Id": sessionId,
        };
        const oidcToken = getStoredOidcToken();
        if (oidcToken) {
          headers.Authorization = `Bearer ${oidcToken}`;
        }
        if (resolvedAccountId) {
          headers["X-ClairAI-Account-Id"] = String(resolvedAccountId).replace(/^aws_/, "");
        }

        const response = await fetch(`${API_BASE_URL}/v1/chat/completions`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            model: model || "prometheus-agent",
            messages: [
              { 
                role: "user", 
                content: prompt.trim() 
              }
            ],
            stream: true,
            max_tokens: 1000,
            temperature: 0.2
          }),
          signal: controller.signal
        });
        console.log('📤 Request sent with model:', model);
        console.log('📥 Response status:', response.status);
        console.log('📥 Response headers:', response.headers.get('content-type'));
        // ============================================
        // CHECK RESPONSE STATUS
        // ============================================
        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          let errorData = {};
          try { 
            errorData = errorText ? JSON.parse(errorText) : {}; 
          } catch { }
          
          const errorMessage = errorData.error 
            || errorData.message 
            || errorData.detail 
            || errorText 
            || `HTTP ${response.status}`;
          
          throw new Error(`HTTP Error (${response.status}): ${errorMessage}`);
        }
        
        // ============================================
        // CHECK IF RESPONSE IS STREAMING
        // ============================================
        const contentType = response.headers.get("content-type");
        const isStreaming = contentType && contentType.includes("text/event-stream");
        
        console.log("Response type:", isStreaming ? "streaming" : "non-streaming");
        
        // ============================================
        // HANDLE NON-STREAMING RESPONSE
        // ============================================
        if (!isStreaming) {
          const text = await response.text();
          let data = {};
          
          try {
            data = text ? JSON.parse(text) : {};
          } catch (parseError) {
            // If not JSON, treat as plain text
            if (typeof text === 'string' && text.trim()) {
              console.log("Received plain text response");
              setFinished(text.trim(), runId, messageId);
              return;
            }
            throw new Error('Invalid response format: not JSON and not plain text');
          }
          
          // Extract content from various possible response formats
          let aiText = "";
          if (Array.isArray(data?.choices) && data.choices[0]?.message?.content) {
            aiText = data.choices[0].message.content;
          } else if (typeof data?.response === 'string') {
            aiText = data.response;
          } else if (typeof data?.content === 'string') {
            aiText = data.content;
          } else {
            throw new Error('Response format not recognized');
          }
          
          console.log("Non-streaming response complete");
          setFinished(aiText, runId, messageId);
          return;
        }
        
        // ============================================
        // HANDLE STREAMING RESPONSE
        // ============================================
        console.log("📊 Starting stream processing...");
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        
        while (true) {
          // Check if user aborted
          if (controller.signal.aborted) {
            console.log("Stream aborted by user");
            reader.cancel();
            throw new Error('Request was aborted');
          }
          
          // Read next chunk
          const { value, done } = await reader.read();
          
          if (done) {
            console.log("Stream complete");
            break;
          }
          
          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete lines from buffer
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer
          
          for (const line of lines) {
            // Only process lines that start with "data: "
            if (!line.startsWith("data: ")) continue;
            
            const dataStr = line.slice(6); // Remove "data: " prefix
            
            // Check for end marker
            if (dataStr === "[DONE]") {
            const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
            const thinkingText = "Analyzing your question about AWS infrastructure. Retrieving relevant metrics and logs from Prometheus and Loki to provide accurate information.";

            // Generate follow-up suggestions
            const followUps = generateFollowUpSuggestions(fullContent);

            if (activeRunIdRef.current === runId) {
              addMessageMetadata(messageId, {
                thinkingProcess: thinkingText,
                responseTime: duration,
                followUpSuggestions: followUps
              });
            }

            setFinished(fullContent, runId, messageId);
              return;
            }
            
            // Parse JSON data
            try {
              const json = JSON.parse(dataStr);
              const delta = json?.choices?.[0]?.delta?.content || "";
              
              if (delta) {
                // Decode unicode characters
                const decodedDelta = decodeUnicodeContent(delta);
                fullContent += decodedDelta;
                
                // Update UI with partial content
                updatePartial(fullContent, runId, messageId);
              }
            } catch (parseError) {
              console.warn("Failed to parse SSE chunk:", parseError);
              // Continue processing other lines
            }
          }
        }
        
        // If we exit loop without [DONE], still finish
        if (fullContent) {
          console.log("Stream ended without [DONE] marker");
          setFinished(fullContent, runId, messageId);
        }
        
      } catch (err) {
        // ============================================
        // ERROR HANDLING
        // ============================================
        
        // User-initiated abort is not an error
        if (err.name === "AbortError" || err.message === "Request was aborted") {
          console.log("Request aborted by user");
          return;
        }
        
        // Real error
        console.error("BackgroundRunner error:", err);
        setError(err.message || "Unknown error occurred", runId, messageId);
      } finally {
        // ============================================
        // CLEANUP
        // ============================================
        isProcessingRef.current = false;
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    };
    
    // Start the task
    runTask();
    
    // Cleanup function (runs when component unmounts or dependencies change)
    return () => {
      if (abortRef.current === controller && !controller.signal.aborted) {
        controller.abort();
      }
    };
    
  }, [
    status,
    prompt,
    model,
    API_BASE_URL,
    sessionId,
    abortRef,
    activeRunIdRef,
    startTimeRef,
    currentMessageIdRef,
    updatePartial,
    setFinished,
    setError,
    addMessageMetadata
  ]); // Re-run when these change
  
  // ============================================
  // RENDER NOTHING
  // ============================================
  return null;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Decode unicode escape sequences in text
 * Example: "\u00e9" → "é"
 */
function decodeUnicodeContent(text) {
  if (typeof text !== 'string') return text;
  return text.replace(/\\u([\da-fA-F]{4})/g, (match, grp) => {
    return String.fromCharCode(parseInt(grp, 16));
  });
}

/**
 * Generate follow-up suggestions based on response content
 */
function generateFollowUpSuggestions(content) {
  const extractedQuestions = [];
  const aiGeneratedSuggestions = [];
  
  // Extract questions from content
  const lines = content.split('\n');
  let inFollowUpSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const lowerLine = line.toLowerCase();
    
    if (lowerLine.includes('follow-up questions') || 
        lowerLine.includes('follow-up:') || 
        lowerLine.includes('related:') || 
        lowerLine.includes('next steps:')) {
      inFollowUpSection = true;
      continue;
    }
    
    if (inFollowUpSection) {
      if (line.startsWith('- ') || line.startsWith('* ')) {
        const question = line.replace(/^[*-]\s+/, '').trim();
        if (question) {
          extractedQuestions.push(question);
        }
      } else if (line && !line.startsWith(' ')) {
        inFollowUpSection = false;
      }
    }
  }
  
  // Add contextual suggestions
  const contentLower = content.toLowerCase();
  
  if (contentLower.includes('error')) {
    aiGeneratedSuggestions.push('Show me recent error patterns');
    aiGeneratedSuggestions.push('What are the most common error types?');
  } else if (contentLower.includes('cost')) {
    aiGeneratedSuggestions.push('How can I optimize AWS costs?');
    aiGeneratedSuggestions.push('Show me cost breakdown by service');
  } else if (contentLower.includes('performance')) {
    aiGeneratedSuggestions.push('What are the performance bottlenecks?');
    aiGeneratedSuggestions.push('Show me latency metrics');
  } else {
    aiGeneratedSuggestions.push('Show me related metrics');
    aiGeneratedSuggestions.push('What are the current health indicators?');
  }
  
  return [...extractedQuestions, ...aiGeneratedSuggestions].slice(0, 3);
}
